import React, { useEffect, useState } from 'react';
import { getSites, getPlugins, getAllPluginsCached, updatePlugin, updateAllPlugins, getExclusions, toggleExclusion, bulkExclusion } from '../api';
import { Puzzle, RefreshCw, Zap, Lock, LockOpen, ChevronDown, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import Flag from '../components/Flag';

const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Plugins() {
  const [sites, setSites]         = useState([]);
  const [plugins, setPlugins]     = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all | updates | uptodate
  const [loading, setLoading]     = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalSites, setTotalSites]   = useState(0);
  const [updating, setUpdating]   = useState({});
  const [excluded, setExcluded]   = useState(new Set()); // `${site_id}|${plugin_file}` keys
  const [lockOpen, setLockOpen]   = useState(false);
  const [lockSearch, setLockSearch] = useState('');

  const exKey = (siteId, file) => `${siteId}|${file}`;
  const isLocked = p => excluded.has(exKey(p.site_id, p.file));

  // Unique plugins (by file) across all loaded sites — for the lock dropdown
  const uniquePlugins = [...new Map(plugins.map(p => [p.file, { file: p.file, name: p.name }])).values()]
    .sort((a, b) => a.name.localeCompare(b.name));
  const lockedNameCount = uniquePlugins.filter(u => plugins.some(p => p.file === u.file && isLocked(p))).length;

  // Lock/unlock one plugin on ALL sites at once (from the dropdown)
  const toggleBulk = async (item) => {
    const instances = plugins.filter(p => p.file === item.file);
    const target = !instances.every(p => isLocked(p)); // lock unless already fully locked
    setExcluded(prev => {
      const next = new Set(prev);
      instances.forEach(p => target ? next.add(exKey(p.site_id, p.file)) : next.delete(exKey(p.site_id, p.file)));
      return next;
    });
    try {
      const r = await bulkExclusion(item.file, target);
      if (r.exclusions) setExcluded(new Set(r.exclusions.map(x => exKey(x.site_id, x.plugin_file))));
      toast.success(target
        ? `${item.name} locked on all sites — updates will skip it`
        : `${item.name} unlocked on all sites`);
    } catch {
      getExclusions().then(rows => setExcluded(new Set(rows.map(r => exKey(r.site_id, r.plugin_file))))).catch(() => {});
      toast.error('Could not save lock');
    }
  };

  const toggleLock = async (p) => {
    const key = exKey(p.site_id, p.file);
    // optimistic flip; revert if the API call fails
    setExcluded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    try {
      const r = await toggleExclusion(p.site_id, p.file);
      toast.success(r.excluded ? `${p.name} locked — bulk/auto updates will skip it` : `${p.name} unlocked`);
    } catch {
      setExcluded(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      });
      toast.error('Could not save lock');
    }
  };

  const [lastSynced, setLastSynced] = useState(null);

  // Instant load from the DB cache — one request, no dependence on the
  // WordPress sites being online. Shown immediately; use Sync for live data.
  const loadCached = async () => {
    getExclusions()
      .then(rows => setExcluded(new Set(rows.map(r => exKey(r.site_id, r.plugin_file)))))
      .catch(() => {});
    try {
      const [s, rows] = await Promise.all([getSites(), getAllPluginsCached()]);
      setSites(s);
      setTotalSites(s.length);
      setPlugins(rows.map(p => ({ ...p, country: p.country || 'Other' })));
      const newest = rows.reduce((m, r) => r.last_synced > m ? r.last_synced : m, '');
      setLastSynced(newest || null);
      // First run ever (empty cache): fall back to a live sync automatically.
      if (rows.length === 0 && s.length > 0) syncLive(s);
    } catch (err) { console.error(err); }
  };

  // Live refresh from every site, appending results as each site responds.
  // Failed sites are skipped; cached rows stay on screen until replaced.
  const syncLive = async (knownSites) => {
    setLoading(true);
    setLoadedCount(0);
    try {
      const s = knownSites || sites.length ? (knownSites || sites) : await getSites();
      setSites(s);
      setTotalSites(s.length);
      const fresh = [];
      await Promise.all(s.map(site =>
        getPlugins(site.id)
          .then(ps => {
            fresh.push(...ps.map(p => ({
              ...p, site_id: site.id, site_name: site.name, country: site.country || 'Other'
            })));
            // replace this site's rows as its live data arrives
            setPlugins(prev => [
              ...prev.filter(x => x.site_id !== site.id),
              ...ps.map(p => ({ ...p, site_id: site.id, site_name: site.name, country: site.country || 'Other' })),
            ]);
          })
          .catch(err => console.warn(`Plugins load failed for ${site.name}:`, err))
          .finally(() => setLoadedCount(c => c + 1))
      ));
      setLastSynced(new Date().toISOString());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCached(); }, []);

  const reload = () => syncLive();

  const handleUpdate = async (siteId, pluginFile, pluginName) => {
    const key = `${siteId}-${pluginFile}`;
    setUpdating(u => ({...u,[key]:true}));
    try {
      await updatePlugin(siteId, pluginFile);
      toast.success(`${pluginName} updated!`);
      setPlugins(prev => prev.map(p =>
        p.site_id===siteId && p.file===pluginFile ? {...p,update_available:false,new_version:null} : p
      ));
    } catch(err) { toast.error(err?.error||'Update failed'); }
    finally { setUpdating(u => ({...u,[key]:false})); }
  };

  const handleUpdateAll = async () => {
    const toUpdate = baseFiltered.filter(p=>p.update_available && !isLocked(p));
    for (const p of toUpdate) {
      await handleUpdate(p.site_id, p.file, p.name);
    }
    toast.success('All updates done!');
  };

  // Unique countries from sites
  const countries = ['all', ...new Set(sites.map(s=>s.country||'Other'))];

  // Country filter → also filter site dropdown options
  const sitesForCountry = selectedCountry==='all'
    ? sites
    : sites.filter(s => (s.country||'Other') === selectedCountry);

  // If selected site not in current country filter, reset to all
  const effectiveSite = sitesForCountry.find(s=>s.id===parseInt(selectedSite)) ? selectedSite : 'all';

  // Apply country + site filters first (status counts are based on this set)
  const baseFiltered = plugins
    .filter(p => selectedCountry==='all' || p.country===selectedCountry)
    .filter(p => effectiveSite==='all' || p.site_id===parseInt(effectiveSite));

  const updatesCount  = baseFiltered.filter(p=>p.update_available).length;
  const uptodateCount = baseFiltered.length - updatesCount;
  const lockedPending = baseFiltered.filter(p=>p.update_available && isLocked(p)).length;
  const pendingCount  = updatesCount - lockedPending; // locked ones are skipped

  // Then apply the status filter
  const filtered = baseFiltered.filter(p =>
    statusFilter==='all' ? true : statusFilter==='updates' ? p.update_available : !p.update_available
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Plugins</h1>
          <p>{loading
            ? `Syncing… ${loadedCount}/${totalSites} sites checked`
            : `${pendingCount} update${pendingCount!==1?'s':''} available${lockedPending ? ` · ${lockedPending} locked` : ''}${lastSynced ? ` · synced ${timeAgo(lastSynced)}` : ''}`}</p>
        </div>
        <div className="header-actions">

          {/* Country filter */}
          <select
            style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,padding:'6px 10px',fontSize:12,color:'var(--text2)'}}
            value={selectedCountry} onChange={e=>setSelectedCountry(e.target.value)}>
            <option value="all">🌍 All countries</option>
            {countries.filter(c=>c!=='all').map(c=><option key={c} value={c}>{c}</option>)}
          </select>

          {/* Site filter — only shows sites matching selected country */}
          <select
            style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,padding:'6px 10px',fontSize:12,color:'var(--text2)'}}
            value={effectiveSite} onChange={e=>setSelectedSite(e.target.value)}>
            <option value="all">All sites</option>
            {sitesForCountry.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <Button className="btn" onClick={reload} loading={loading}><RefreshCw size={13}/> {loading?'Syncing...':'Sync'}</Button>
          <Button className="btn primary" onClick={handleUpdateAll} disabled={!pendingCount}><Zap size={13}/> Update all ({pendingCount})</Button>
        </div>
      </div>

      <div className="page-content">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:14 }}>
          <div className="seg">
            <button className={statusFilter==='all'?'active':''} onClick={()=>setStatusFilter('all')}>
              All <span className="seg-count">{baseFiltered.length}</span>
            </button>
            <button className={statusFilter==='updates'?'active':''} onClick={()=>setStatusFilter('updates')}>
              <span className="seg-dot" style={{background:'var(--amber)'}}></span> Updates available <span className="seg-count">{updatesCount}</span>
            </button>
            <button className={statusFilter==='uptodate'?'active':''} onClick={()=>setStatusFilter('uptodate')}>
              <span className="seg-dot" style={{background:'var(--green)'}}></span> Up to date <span className="seg-count">{uptodateCount}</span>
            </button>
          </div>

          {/* Lock-plugins dropdown: selected plugins are skipped by all updates */}
          <div style={{ position:'relative' }}>
            <button className="btn" onClick={()=>setLockOpen(o=>!o)}>
              <Lock size={13}/> Lock plugins
              {lockedNameCount > 0 && <span className="seg-count" style={{background:'var(--amber-bg)',color:'var(--amber-text)'}}>{lockedNameCount}</span>}
              <ChevronDown size={13}/>
            </button>
            {lockOpen && (
              <>
                <div onClick={()=>setLockOpen(false)} style={{ position:'fixed', inset:0, zIndex:9 }} />
                <div style={{ position:'absolute', right:0, top:'calc(100% + 6px)', zIndex:10, width:330, background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:14, boxShadow:'var(--shadow)', padding:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7, background:'var(--bg3)', border:'1px solid var(--border2)', borderRadius:9, padding:'7px 10px' }}>
                    <Search size={13} color="var(--text3)" />
                    <input value={lockSearch} onChange={e=>setLockSearch(e.target.value)} placeholder="Search plugins…" autoFocus
                      style={{ flex:1, background:'none', border:'none', outline:'none', color:'var(--text)', fontSize:12.5 }} />
                  </div>
                  <div style={{ maxHeight:280, overflowY:'auto', marginTop:8 }}>
                    {uniquePlugins.filter(u => u.name.toLowerCase().includes(lockSearch.toLowerCase())).map(item => {
                      const instances = plugins.filter(p => p.file === item.file);
                      const lockedCount = instances.filter(p => isLocked(p)).length;
                      const all  = instances.length > 0 && lockedCount === instances.length;
                      const some = lockedCount > 0 && !all;
                      return (
                        <label key={item.file} style={{ display:'flex', alignItems:'center', gap:9, padding:'7px 9px', borderRadius:8, cursor:'pointer', background: all ? 'var(--amber-bg)' : 'transparent' }}>
                          <input type="checkbox" checked={all} ref={el=>{ if (el) el.indeterminate = some; }} onChange={()=>toggleBulk(item)} style={{ accentColor:'var(--amber)' }} />
                          <span style={{ flex:1, fontSize:12.5, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</span>
                          {all  && <Lock size={11} color="var(--amber)" />}
                          {some && <span className="badge amber" style={{fontSize:9}}>{lockedCount}/{instances.length}</span>}
                          <span style={{ fontSize:10, color:'var(--text3)', flexShrink:0 }}>{instances.length} site{instances.length!==1?'s':''}</span>
                        </label>
                      );
                    })}
                    {uniquePlugins.length === 0 && (
                      <div style={{ padding:'16px 0', textAlign:'center', fontSize:12, color:'var(--text3)' }}>{loading ? 'Loading plugins…' : 'No plugins loaded yet'}</div>
                    )}
                  </div>
                  <div style={{ fontSize:10.5, color:'var(--text3)', marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)' }}>
                    ✓ Locked plugins are skipped by “Update all” and scheduled auto-updates — on every site.
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="card">
          <table className="tbl">
            <thead>
              <tr><th>Plugin</th><th>Site</th><th>Country</th><th>Current</th><th>Latest</th><th>Status</th><th>Active</th><th>Action</th></tr>
            </thead>
            <tbody>
              {filtered.length===0 && (
                <tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>
                  {loading ? `Syncing plugins… ${loadedCount}/${totalSites} sites checked` : 'No plugins found — press Sync to fetch from your sites.'}
                </td></tr>
              )}
              {filtered.map((p,i) => {
                const key = `${p.site_id}-${p.file}`;
                const locked = isLocked(p);
                return (
                  <tr key={i} style={locked ? {opacity:.65} : undefined}>
                    <td className="bold">{p.name}</td>
                    <td style={{color:'var(--text3)',fontSize:11}}>{p.site_name}</td>
                    <td><span style={{display:'inline-flex',alignItems:'center',gap:7}}><Flag country={p.country}/> <span style={{fontSize:11,color:'var(--text3)'}}>{p.country}</span></span></td>
                    <td>{p.version}</td>
                    <td style={{color:p.update_available?'var(--green-text)':'var(--text3)'}}>{p.new_version||p.version}</td>
                    <td>
                      {locked
                        ? <span className="badge gray"><Lock size={10}/> Locked</span>
                        : <span className={`badge ${p.update_available?'amber':'green'}`}>{p.update_available?'Update available':'Up to date'}</span>}
                    </td>
                    <td><span className={`badge ${p.active?'blue':'gray'}`}>{p.active?'Active':'Inactive'}</span></td>
                    <td>
                      <div style={{display:'flex',gap:4,alignItems:'center'}}>
                        {p.update_available && !locked && (
                          <Button className="btn sm success" onClick={()=>handleUpdate(p.site_id,p.file,p.name)} loading={updating[key]}>
                            <RefreshCw size={11}/> {updating[key]?'Updating...':'Update'}
                          </Button>
                        )}
                        <Button
                          className={`btn sm${locked ? ' amber' : ''}`}
                          onClick={()=>toggleLock(p)}
                          title={locked ? 'Unlock — allow updates again' : 'Lock — skip in bulk & scheduled updates'}>
                          {locked ? <Lock size={11}/> : <LockOpen size={11}/>}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
